import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

/**
 * Компонент TimezoneConverterPage - страница конвертера часовых поясов
 */
const TimezoneConverterPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();
  
  // Получаем данные инструмента
  const tool = getToolById('timezone-converter');
  
  // Устанавливаем заголовок страницы
  useEffect(() => {
    document.title = tool?.metaTitle || 'Конвертер часовых поясов | Gensio';
    
    // Находим мета-описание и устанавливаем его, если есть
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription && tool?.metaDescription) {
      metaDescription.setAttribute('content', tool.metaDescription);
    }
  }, [tool]);
  
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200">
      {/* Фоновый узор */}
      <div className="fixed inset-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] dark:bg-[radial-gradient(#1f2937_1px,transparent_1px)] [background-size:20px_20px] opacity-25 pointer-events-none"></div>
      
      {/* Навигационная панель */}
      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
      
      {/* Основное содержимое */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Хлебные крошки */}
        <Breadcrumbs />
        
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Основной контент */}
          <div className="lg:w-2/3">
            {/* Заголовок и описание */}
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                {tool?.name || 'Конвертер часовых поясов'}
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                {tool?.description || 'Проверка времени в разных городах мира с учетом летнего времени и часовых зон.'}
              </p>
            </div>
            
            {/* Заглушка для контента */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-8">
              <div className="p-12 text-center">
                <p className="text-xl text-gray-600 dark:text-gray-400">
                  Конвертер часовых поясов находится в разработке. Скоро здесь появится полная функциональность.
                </p>
              </div>
            </div>
            
            {/* Информация об инструменте */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mt-8">
              <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
                О конвертере часовых поясов
              </h2>
              
              <div className="prose dark:prose-invert max-w-none">
                <p>
                  Конвертер часовых поясов позволяет узнать точное время в любом городе мира и легко конвертировать время между разными часовыми поясами. Инструмент учитывает переход на летнее и зимнее время в разных странах.
                </p>
                
                <p>
                  С помощью нашего конвертера вы сможете:
                </p>
                
                <ul>
                  <li>Узнать текущее время в любом городе мира</li>
                  <li>Конвертировать время между разными часовыми поясами</li>
                  <li>Планировать международные встречи и звонки</li>
                  <li>Находить удобное время для связи с людьми из разных стран</li>
                  <li>Учитывать переход на летнее и зимнее время</li>
                </ul>
                
                <p>
                  Инструмент будет особенно полезен путешественникам, людям, работающим с международными командами, а также всем, кто общается с друзьями и родственниками в других странах.
                </p>
                
                <p>
                  <strong>Часовые пояса:</strong>
                </p>
                
                <p>
                  Земля разделена на 24 основных часовых пояса, которые отличаются на один час друг от друга. Точкой отсчета служит Всемирное координированное время (UTC), которое раньше называлось Гринвичским средним временем (GMT). Некоторые страны и регионы используют нестандартные смещения (например, +5:30 или +8:45).
                </p>
                
                <p>
                  Скоро здесь появится полностью функциональный конвертер часовых поясов с интерактивной картой и дополнительными возможностями.
                </p>
              </div>
            </div>
          </div>
          
          {/* Боковая панель */}
          <div className="lg:w-1/3">
            <ToolSidebar 
              currentToolId="timezone-converter"
              currentSubToolId={subToolId}
              tool={tool}
            />
          </div>
        </div>
      </main>
      
      {/* Футер */}
      <Footer />
    </div>
  );
};

export default TimezoneConverterPage;